import { Text } from "@hackthenorth/north";
import { DocumentCheckIcon } from "@heroicons/react/24/outline";
import { Button } from "north.js";
import React, { useState } from "react";
import styled, { keyframes } from "styled-components";

import { errorToast, successToast } from "src/shared/components";
import { useUserContext } from "src/shared/contexts";
import {
  ChatIcon,
  ChipIcon,
  EyeIcon,
  EyeOffIcon,
  GearIcon,
  NetworkIcon,
  QRCodeIcon,
} from "src/static/icons";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import { useDeleteGooseGameQuestMutation } from "src/views/goose-games/graphql/deleteGooseGameQuest.generated";
import { useUpdateGooseGameQuestMutation } from "src/views/goose-games/graphql/updateGooseGameQuests.generated";
import {
  Quest,
  QuestBackgroundColorMap,
  QuestCardColors,
  QuestPrimaryColorMap,
  QuestToGooseTypeMap,
  QuestType,
} from "src/views/goose-games/types";

import { BalanceChip } from "../../balanceChip";
import QuestModal from "../../modals/hacker/home/quest";
import { QUESTS } from "../../modals/hacker/home/quest/constants";
import EditModal from "../../modals/organizer/quest/edit";

const QuestCard = ({
  quest,
  view = "quests",
  progress,
  isApproved = false,
  onCompleteClick,
  onToggleHidden,
  refetchQuests = () => {},
}: {
  quest: Quest;
  view?: "quests" | "scan" | "player";
  progress?: number;
  isApproved?: boolean;
  onCompleteClick?: () => void;
  onToggleHidden?: (quest: Quest, hidden: boolean) => void;
  refetchQuests?: () => void;
}) => {
  const [updateGooseGameQuest] = useUpdateGooseGameQuestMutation();
  const [deleteGooseGameQuest] = useDeleteGooseGameQuestMutation();
  const { isOrganizer } = useUserContext();
  const canManage = isOrganizer && view !== "player";
  const completedQuest =
    (progress !== undefined &&
      progress >= quest.completion_limit &&
      !canManage) ||
    isApproved;
  const [modalOpen, setModalOpen] = useState(false);

  const handleEditQuest = async (
    id: number,
    successMessage: string,
    errorMessage: string,
    flash: boolean,
    hidden: boolean,
    title?: string,
    type?: QuestType,
    pointsRewarded?: number,
    completionLimit?: number,
    eventCategory?: string,
    description?: string,
    questionString?: string,
    questionAnswer?: string,
    meta?: Record<string, unknown> | null
  ) => {
    try {
      await updateGooseGameQuest({
        variables: {
          id: id,
          title: title,
          type: type ? QuestToGooseTypeMap[type] : undefined,
          pointsRewarded: pointsRewarded,
          completionLimit: completionLimit,
          questEventActivityCategory: eventCategory,
          description: description,
          questionString: questionString,
          questionAnswer: questionAnswer,
          flash: flash,
          hidden: hidden,
          meta,
        },
      });
      successToast(successMessage);
    } catch (error) {
      errorToast(errorMessage);
      console.error(error);
    } finally {
      refetchQuests();
    }
    setModalOpen(false);
  };

  const handleDeleteQuest = async (
    id: number,
    successMessage: string,
    errorMessage: string
  ) => {
    try {
      await deleteGooseGameQuest({
        variables: {
          id: id,
        },
      });
      successToast(successMessage);
    } catch (error) {
      errorToast(errorMessage);
      console.error(error);
    } finally {
      refetchQuests();
    }
    setModalOpen(false);
  };

  return (
    <>
      {modalOpen &&
        (canManage ? (
          <EditModal
            quest={quest}
            isOpen={modalOpen}
            onDelete={handleDeleteQuest}
            onConfirm={handleEditQuest}
            onCancel={() => setModalOpen(false)}
          />
        ) : (
          <QuestModal
            quest={quest}
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            refetchQuest={refetchQuests}
            progress={progress}
          />
        ))}
      <Card
        hasAnimatedBorder={quest.flash && !quest.hidden}
        onClick={modalOpen ? () => {} : () => setModalOpen(true)}
      >
        <FlexHeading>
          <TypeContainer>
            <IconOutline type={quest.type} isHidden={!quest.hidden}>
              {quest.type === QuestType.Scanned ? (
                <QRCodeIcon width={24} color={QUESTS.SCANNED.color} />
              ) : quest.type === QuestType.OrganizerApproved ? (
                <GearIcon width={24} />
              ) : quest.type === QuestType.Question ? (
                <ChatIcon width={24} />
              ) : quest.type === QuestType.Badge ? (
                <ChipIcon width={24} />
              ) : (
                <NetworkIcon width={24} />
              )}
            </IconOutline>
            {completedQuest && (
              <StatusOutline>
                <StatusText mods="p">Complete</StatusText>
              </StatusOutline>
            )}
          </TypeContainer>
          <BalanceChip
            isComplete={completedQuest || quest.hidden}
            points={quest.points_rewarded}
            completionLimit={quest.completion_limit}
          />
        </FlexHeading>
        <TitleText
          mods="heading h3"
          isFlash={quest.flash && !quest.hidden}
          isHidden={!quest.hidden}
        >
          {quest.title}
        </TitleText>
        <ContentContainer>
          {canManage ? (
            quest.completion_limit > 1 ? (
              <>
                <IndicatorContainer>
                  {Array.from(Array(quest.completion_limit), (i) => (
                    <IndicatorNub
                      key={`Indicator ${quest.id} ${i}`}
                      status={"disabled"}
                    />
                  ))}
                </IndicatorContainer>
                <PointsText mods="p">
                  Maximum {quest.completion_limit} scan
                  {quest.completion_limit === 1 ? "" : "s"}
                </PointsText>
              </>
            ) : (
              <ContentContainer>
                <SubtitleText mods="p" isHidden={!quest.hidden}>
                  {quest.description}
                </SubtitleText>
                {quest.type === QuestType.OrganizerApproved && view === "scan" && (
                  <>
                    <ButtonSpacer />
                    <MarkButton
                      leadingIcon={<DocumentCheckIcon width={20} />}
                      size="md"
                      color="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCompleteClick?.();
                      }}
                    >
                      {completedQuest
                        ? 'Mark "Not Complete"'
                        : 'Mark "Complete"'}
                    </MarkButton>
                  </>
                )}
              </ContentContainer>
            )
          ) : quest.completion_limit > 1 ? (
            <>
              <IndicatorContainer>
                {completedQuest
                  ? Array.from(Array(quest.completion_limit), (i) => (
                      <IndicatorNub
                        key={`Indicator ${quest.id} ${i}`}
                        status={"allCompleted"}
                      />
                    ))
                  : Array.from(Array(quest.completion_limit), (_, i) => (
                      <IndicatorNub
                        key={`Indicator ${quest.id} ${i}`}
                        status={
                          progress !== undefined && i < progress
                            ? "completed"
                            : "incompleted"
                        }
                      />
                    ))}
              </IndicatorContainer>
              <PointsText mods="p">
                {progress === undefined ? 0 : progress}/{quest.completion_limit}{" "}
                scanned
              </PointsText>
            </>
          ) : (
            <SubtitleText mods="p" isHidden={!quest.hidden}>
              {quest.description}
            </SubtitleText>
          )}
          {isOrganizer && onToggleHidden && (
            <>
              <ButtonSpacer />
              <MarkButton
                leadingIcon={
                  quest.hidden ? (
                    <EyeIcon width={20} />
                  ) : (
                    <EyeOffIcon width={20} />
                  )
                }
                size="md"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleHidden(quest, !quest.hidden);
                }}
              >
                {quest.hidden ? "Show for Hackers" : "Hide from Hackers"}
              </MarkButton>
            </>
          )}
        </ContentContainer>
      </Card>
    </>
  );
};

const FlexHeading = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const IconOutline = styled.div<{
  type: QuestType;
  isHidden: boolean;
}>`
  background: ${({ type }) => QuestBackgroundColorMap[type]};
  color: ${({ type }) => QuestPrimaryColorMap[type]} !important;
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  padding: 4px;
  border-radius: 32px;
  height: 32px;
  opacity: ${({ isHidden }) => (isHidden ? 1 : 0.5)};
`;

const TypeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const MarkButton = styled(Button)`
  color: ${({ theme }) => theme.globalConstants.color.greyPrimary} !important;
  font-weight: 700;
  position: absolute;
  bottom: 0px;
  left: 0px;
`;

const ButtonSpacer = styled.div`
  width: 100%;
  height: 36px;
`;

const StatusOutline = styled.div`
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundSuccessLight};
  display: flex;
  padding: 4px 9.189px;
  align-items: center;
  gap: 4px;
  border-radius: 32px;
  height: 32px;
`;

const StatusText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textSuccess} !important;
  font-size: 14px;
  font-weight: 700;
`;

const TitleText = styled(Text)<{ isFlash: boolean; isHidden: boolean }>`
  color: ${({ isFlash }) =>
    isFlash ? QuestCardColors.flashTitle : QuestCardColors.title} !important;
  line-height: normal !important;
  opacity: ${({ isHidden }) => (isHidden ? 1 : 0.5)};
`;

const IndicatorContainer = styled.div`
  width: 100%;
  height: 10px;
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const IndicatorNub = styled.div<{
  status: "allCompleted" | "completed" | "incompleted" | "disabled";
}>`
  width: 100%;
  height: 100%;
  border-radius: 32px;
  background: ${({ status, theme }) =>
    status === "allCompleted"
      ? theme.globalConstants.color.backgroundSuccess
      : status === "completed"
      ? theme.globalConstants.color.backgroundPrimary
      : status === "disabled"
      ? theme.globalConstants.color.backgroundGrayDark
      : theme.globalConstants.color.backgroundPrimaryLight};
`;

const SubtitleText = styled(Text)<{ isHidden: boolean }>`
  color: ${QuestCardColors.subtitle} !important;
  line-height: normal !important;
  overflow: hidden;
  display: -webkit-box !important; /* stylelint-disable-line value-no-vendor-prefix */
  -webkit-box-orient: vertical; /* stylelint-disable-line property-no-vendor-prefix */
  line-clamp: 2;
  -webkit-line-clamp: 2; /* stylelint-disable-line property-no-vendor-prefix */
  text-overflow: ellipsis;
  opacity: ${({ isHidden }) => (isHidden ? 1 : 0.5)};

  ${mediaQueries.medium} {
    line-clamp: 4;
    -webkit-line-clamp: 4; /* stylelint-disable-line property-no-vendor-prefix */
  }
`;

const PointsText = styled(Text)`
  color: ${QuestCardColors.points} !important;
  font-size: 12px !important;
  font-style: normal;
  font-weight: 500 !important;
  line-height: normal;
`;

const rotateBorder = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const Card = styled.div<{ hasAnimatedBorder: boolean }>`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;
  width: 100%;
  padding: 16px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  border-radius: 8px;
  border: ${({ hasAnimatedBorder }) =>
    hasAnimatedBorder
      ? "1px solid transparent"
      : `1px solid var(--North-Border-Gray, ${QuestCardColors.border})`};
  box-shadow: ${({ hasAnimatedBorder }) =>
    hasAnimatedBorder ? "0 2px 8px rgba(216, 176, 0, 0.18)" : "none"};
  background: ${({ theme }) => theme.globalConstants.color.white};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &::before {
    content: ${({ hasAnimatedBorder }) => (hasAnimatedBorder ? '""' : "none")};
    position: absolute;
    z-index: -2;
    inset: -100%;
    background: conic-gradient(
      ${QuestCardColors.flashBorderDark} 0%,
      ${QuestCardColors.flashBorderDark} 40%,
      ${QuestCardColors.flashBorderLight} 50%,
      ${QuestCardColors.flashBorderLighter} 60%,
      ${QuestCardColors.flashBorderLightest} 68%,
      ${QuestCardColors.flashHighlight} 72%,
      ${QuestCardColors.flashBorderLightest} 76%,
      ${QuestCardColors.flashBorderLighter} 84%,
      ${QuestCardColors.flashBorderLight} 92%,
      ${QuestCardColors.flashBorderDark} 100%
    );
    animation: ${rotateBorder} 3s linear infinite;
  }

  &::after {
    content: ${({ hasAnimatedBorder }) => (hasAnimatedBorder ? '""' : "none")};
    position: absolute;
    z-index: -1;
    inset: 1px;
    border-radius: 7px;
    background: ${({ theme }) => theme.globalConstants.color.white};
    transition: background-color 0.2s ease;
  }

  &:hover {
    background-color: ${QuestCardColors.hover};
  }

  &:hover::after {
    background-color: ${QuestCardColors.hover};
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

export default QuestCard;
